import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useAccount } from '@/hooks/useAccount'
import { formatDate, formatDateTime, formatMonthYear, type CalendarSystem } from '@/lib/date'

/**
 * LIFF-H5 — account-aware date formatters.
 *
 * Returns formatters bound to the resolved account's timezone / date_format /
 * calendar (served by /api/liff/resolve). Replaces the per-page
 * toLocaleDateString('th-TH') copies that ignored account config entirely.
 */
export function useDateFormatter() {
  const { account } = useAccount()
  const { i18n } = useTranslation()

  const timezone = account?.timezone
  const format = account?.date_format
  const calendar = account?.calendar as CalendarSystem | undefined
  const locale = i18n.language?.startsWith('en') ? 'en' : 'th'

  /** Numeric date per the account layout/zone/era (e.g. "10/06/2569"). */
  const formatDateFn = useCallback(
    (dateString: string | null | undefined) =>
      formatDate(dateString, { format, timezone, calendar }),
    [format, timezone, calendar],
  )

  /** Date + 24h time per the account layout/zone/era (e.g. "10/06/2569 14:30"). */
  const formatDateTimeFn = useCallback(
    (dateString: string | null | undefined) =>
      formatDateTime(dateString, { format, timezone, calendar }),
    [format, timezone, calendar],
  )

  /** Short month + year per UI locale + account era (e.g. "มิ.ย. 2569"). */
  const formatMonthYearFn = useCallback(
    (dateString: string | null | undefined) =>
      formatMonthYear(dateString, { locale, calendar }),
    [locale, calendar],
  )

  return {
    formatDate: formatDateFn,
    formatDateTime: formatDateTimeFn,
    formatMonthYear: formatMonthYearFn,
  }
}
